// src/components/condos/CondoCard.tsx
// Card for listing a condo in user-facing Discover Condos grid

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Condo } from '../../types/condo';
import SignUpPromptModal from '../common/SignUpPromptModal';

interface CondoCardProps {
  condo: Condo;
  onCheckWaitlist?: (condoId: string) => void;
  isAuthenticated?: boolean;
}

const CondoCard: React.FC<CondoCardProps> = ({ condo, onCheckWaitlist, isAuthenticated = true }) => {
  const navigate = useNavigate();
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  const condoId = condo.id || condo._id || '';
  const resolveSource = (c: typeof condo) =>
    c.createdVia ?? (c.sourceUrl ? 'scraper' : c.submittedBy ? 'user' : 'admin');
  const isBackend = resolveSource(condo) === 'backend';
  const primaryImage =
    condo.images && condo.images.length > 0 ? condo.images[0] : null;
  const locationText =
    [condo.location?.neighborhood, condo.location?.borough].filter(Boolean).join(', ') ||
    condo.location?.borough ||
    '—';
  const priceText = condo.price != null ? `$${condo.price.toLocaleString()} / mo` : '—';
  const bedroomsText = condo.bedrooms === 0 ? 'Studio' : condo.bedrooms != null ? `${condo.bedrooms} bed` : null;
  const bathroomsText = condo.bathrooms != null ? `${condo.bathrooms} bath` : null;
  const sqftText = condo.sqft != null ? `${condo.sqft.toLocaleString()} sqft` : null;
  const unitsSummary = [bedroomsText, bathroomsText, sqftText].filter(Boolean).join(' · ') || '—';
  const waitlistText =
    condo.waitlistCount != null ? `${condo.waitlistCount} in interest group` : '';

  const handleCardClick = () => {
    if (condoId) navigate(`/condos/${condoId}`);
  };

  const handleCheckWaitlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowSignUpModal(true);
      return;
    }
    if (condoId && onCheckWaitlist) onCheckWaitlist(condoId);
  };

  const handleViewDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (condoId) navigate(`/condos/${condoId}`);
  };

  return (
    <>
    <div
      className="relative bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
      role="button"
      tabIndex={0}
    >
      {isBackend && (
        <div className="absolute top-4 -left-8 w-32 bg-amber-500 text-white text-[11px] font-bold text-center py-1 -rotate-45 shadow-md z-10">
          ⚠ Demo listing
        </div>
      )}
      {/* Primary image */}
      <div className="mb-4 rounded-lg overflow-hidden border-2 border-coffee">
        {primaryImage ? (
          <img src={primaryImage} alt={condo.name} className="w-full h-48 object-cover" />
        ) : (
          <div className="w-full h-48 bg-cream flex items-center justify-center">
            <span className="text-coffee/50 text-sm font-medium">No Image</span>
          </div>
        )}
      </div>

      {/* Free months badge */}
      {(condo.freeMonths ?? 0) > 0 && (
        <div className="mt-1 mb-2">
          <span className="bg-terracotta text-white text-xs font-semibold px-2 py-0.5 rounded-full border border-coffee">
            ★ {condo.freeMonths} month{condo.freeMonths! > 1 ? 's' : ''} free
          </span>
        </div>
      )}

      {/* Name + source badge */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-coffee font-bold text-lg">{condo.name}</h3>
        {condo.source && (
          <span className="inline-block bg-steel text-white text-xs font-medium px-2 py-0.5 rounded-full border border-coffee">
            StreetEasy
          </span>
        )}
      </div>

      {/* Location */}
      <div className="mb-3">
        <span className="text-coffee/80 font-medium text-sm">Location: </span>
        <span className="text-coffee text-sm">{locationText}</span>
        {condo.location?.address && (
          <div className="text-coffee/60 text-xs mt-0.5">{condo.location.address}</div>
        )}
      </div>

      {/* Price */}
      <div className="mb-3">
        <span className="text-coffee/80 font-medium text-sm">Price: </span>
        <span className="text-coffee text-sm">{priceText}</span>
      </div>

      {/* Unit details and waitlist */}
      <div className="mb-4">
        <span className="text-coffee text-sm">{unitsSummary}</span>
        {waitlistText && (
          <span className="text-coffee text-sm ml-1"> · {waitlistText}</span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleCheckWaitlist}
          className="flex-1 min-w-[100px] text-sm bg-terracotta text-white border-2 border-coffee rounded-lg shadow-[2px_2px_0px_#594A42] hover:translate-y-[-1px] active:translate-y-[1px] active:shadow-none transition-all px-3 py-2 font-medium"
        >
          Interest Group
        </button>
        <button
          type="button"
          onClick={handleViewDetail}
          className="text-sm bg-white border-2 border-coffee text-coffee rounded-lg px-3 py-2 font-medium hover:bg-cream transition-colors"
        >
          View Detail
        </button>
      </div>
    </div>

    <SignUpPromptModal
      isOpen={showSignUpModal}
      onClose={() => setShowSignUpModal(false)}
      message="Sign up to explore listings, view details, and join interest groups"
      condoId={condoId}
    />
    </>
  );
};

export default CondoCard;
